import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useUser } from './context/UserContext';
import { ROUTES } from './constants/Routes';
import { ENABLE_PROCUREMENT_PLANNER } from './constants/featureFlags';
import { usePageTracking } from './utils/analytics';
import PageSkeleton from './components/PageSkeleton';

// Auth Pages
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import EmailVerification from './pages/EmailVerification';
import SignUpSuccess from './pages/SignUpSuccess';
import AuthCallback from './pages/AuthCallback';
// Lazy-loaded: keeps @stytch/* and its module-scope Stytch client init out of the
// main bundle so they only load when a user actually hits /oauth/authorize.
const OAuthAuthorize = React.lazy(() => import('./pages/OAuthAuthorize'));

// Main App Pages with DashboardLayout
import DashboardLayout from './pages/DashboardLayout';
import Analytics from './pages/Analytics';
import BiQ from './pages/BiQ';
import Account from './pages/Account';
import Privacy from './pages/Privacy';
import Billing from './pages/Billing';
import Usage from './pages/Usage';
import BloomTerminalFAQ from './pages/BloomTerminalFAQ';
import ContactUs from './pages/ContactUs';
import LookerStudio from './pages/LookerStudio';
import ManageMembership from './pages/ManageMembership';
import Home from './pages/Home';
import Paywall from './pages/Paywall';

// Alerts Pages
import AlertsHub from './pages/alerts/AlertsHub';
import Alerts from './pages/alerts/Alerts';
import AlertEdit from './pages/alerts/AlertEdit';

// Map (unified sea tracking + weather)
import MapPage from './pages/MapPage';

// Produce Planner is dev-only (see featureFlags.js). The import() MUST be guarded
// by the INLINE `import.meta.env` literal here — not the imported
// ENABLE_PROCUREMENT_PLANNER constant — because Vite/Rolldown only folds the inline
// literal to `false` and tree-shakes the dynamic import, dropping the
// ProcurementPlanner chunk from the prod bundle entirely. A cross-module imported
// constant is NOT inlined for dynamic-import elimination (verified: it leaks the
// chunk into prod), so keep this expression inline.
const ProcurementPlanner =
  import.meta.env.VITE_ENABLE_PROCUREMENT_PLANNER === 'true'
    ? React.lazy(() => import('./pages/ProcurementPlanner'))
    : null;

function LoadingSpinner() {
  const { pathname } = useLocation();
  const shape = pathname.includes('/map') ? 'map'
    : (pathname === '/' || pathname.includes('/biq') || pathname.includes('/chatbot')) ? 'chat'
    : pathname.includes('/analytics') ? 'chart'
    : pathname.includes('/alerts') ? 'alerts'
    : pathname.includes('/account') ? 'form'
    : pathname.includes('/usage') ? 'usage'
    : pathname.includes('/subscription') ? 'plans'
    : 'page';
  return <PageSkeleton variant="full" shape={shape} />;
}

// Protected Route Wrapper
function ProtectedRoute({ children }) {
  const { firebaseUser, authChecked } = useUser();

  if (!authChecked) {
    return <LoadingSpinner />;
  }

  if (!firebaseUser) {
    // Unauthenticated: send to canonical English login. Users who had a
    // Spanish preference will see it reapplied by Login.jsx on mount via
    // applyPathLanguage if they later land on /es/login.
    return <Navigate to="/en/login" replace />;
  }

  return children;
}

/**
 * Legacy `/auth/callback` handler: old bloom-website builds (and any cached
 * pages) still emit `app/auth/callback?lang=es#code=...`. Preserve the hash
 * (which carries the one-time auth code) and forward to the locale-prefixed
 * canonical route.
 */
function LegacyAuthCallbackRedirect() {
  const search = new URLSearchParams(window.location.search);
  const lang = search.get('lang') === 'es' ? 'es' : 'en';
  return <Navigate to={`/${lang}/auth/callback${window.location.hash}`} replace />;
}

// Public Route Wrapper (redirects authenticated users to dashboard)
function PublicRoute({ children }) {
  const { firebaseUser, authChecked, isRegistering } = useUser();

  if (!authChecked) {
    return <LoadingSpinner />;
  }

  // Skip redirect while registration is in progress — the signup flow
  // temporarily authenticates the user before signing them back out.
  if (firebaseUser && !isRegistering) {
    // Default authenticated landing page is the root BiQ route.
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  // Track page views on route changes
  usePageTracking();

  return (
    <Routes>
      {/* Locale-prefixed auth routes (canonical). React Router v6 has no
          inline regex params, so we declare one Route per supported locale
          via a fragment map — clean, DRY, and avoids runtime validation
          inside the page components. */}
      {['en', 'es'].map((l) => (
        <React.Fragment key={l}>
          <Route
            path={`/${l}/login`}
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path={`/${l}/signup`}
            element={
              <PublicRoute>
                <SignUp />
              </PublicRoute>
            }
          />
          <Route path={`/${l}/forgot-password`} element={<ForgotPassword />} />
          <Route path={`/${l}/auth/callback`} element={<AuthCallback />} />
        </React.Fragment>
      ))}

      {/* Flat auth routes → redirect to /en/* (Navigate preserves search + hash).
          Kept for backward compat with bookmarks, old emails and cached pages. */}
      <Route path="/login" element={<Navigate to="/en/login" replace />} />
      <Route path="/signup" element={<Navigate to="/en/signup" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/en/forgot-password" replace />} />
      <Route path="/auth/callback" element={<LegacyAuthCallbackRedirect />} />

      {/* Out of scope for locale prefix — stay flat. */}
      <Route path={ROUTES.EMAIL_VERIFICATION} element={<EmailVerification />} />
      <Route path={ROUTES.SIGN_UP_SUCCESS} element={<SignUpSuccess />} />

      {/* OAuth consent screen — PUBLIC and NON-localized. Stytch's
          Authorization URL has no locale segment and sends unauthenticated
          users here directly; OAuthAuthorize does its own auth check and
          redirect, so it must NOT be wrapped in ProtectedRoute. */}
      <Route
        path={ROUTES.OAUTH_AUTHORIZE}
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <OAuthAuthorize />
          </Suspense>
        }
      />

      {/* Protected routes with Dashboard Layout */}
      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Main app routes */}
        <Route index element={<BiQ />} />
        <Route path="/overview" element={<Home />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/terminal" element={<Navigate to="/analytics" replace />} />
        <Route path="/lookerstudio" element={<LookerStudio />} />
        <Route path="/biq" element={<BiQ />} />
        <Route path="/chatbot" element={<BiQ />} />
        {/* Produce Planner: rendered only in dev (flag on). In prod the flag is
            off, so the chunk is never bundled and the route redirects to BiQ. */}
        <Route
          path={ROUTES.PROCUREMENT}
          element={
            ENABLE_PROCUREMENT_PLANNER && ProcurementPlanner ? (
              <React.Suspense fallback={<LoadingSpinner />}>
                <ProcurementPlanner />
              </React.Suspense>
            ) : (
              <Navigate to={ROUTES.BIQ} replace />
            )
          }
        />

        {/* Map */}
        <Route path="/map" element={<MapPage />} />
        <Route path="/sea-tracking" element={<Navigate to="/map" replace />} />

        {/* Alerts routes — hub at /alerts; configure wizard separate */}
        <Route path="/alerts" element={<AlertsHub />} />
        <Route path="/alerts/configure" element={<Alerts />} />
        <Route path="/alerts/enabled" element={<Navigate to="/alerts#enabled" replace />} />
        <Route path="/alerts/triggered" element={<Navigate to="/alerts#triggered" replace />} />
        <Route path="/alerts/edit/:alertId" element={<AlertEdit />} />

        {/* Account routes */}
        <Route path="/account" element={<Account />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/usage" element={<Usage />} />
        <Route path="/membership" element={<ManageMembership />} />
        <Route path="/subscription" element={<Paywall />} />

        {/* Help routes */}
        <Route path="/faq" element={<BloomTerminalFAQ />} />
        <Route path="/contact" element={<ContactUs />} />
      </Route>

      {/* Catch-all redirect to login */}
      <Route path="*" element={<Navigate to="/en/login" replace />} />
    </Routes>
  );
}

export default App;
